import {
  FC,
  FormEvent,
  Fragment,
  MouseEvent,
  ReactNode,
  useEffect,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import "./Operator-request-status.css";
import { AppDispatch, RootState } from "../../../redux/Store";
import { getApplicationInfo } from "../../../redux/requests/ApplicationRequests";
import { applicationSlice } from "../../../redux/reducers/ApplicationSlice";

type SearchType = "id" | "order_id" | "vehicle_number" | "vin";
type Variant = "neutral" | "success" | "warning" | "danger" | "info" | "purple";

interface StateInfo {
  state: string;
  short: string;
  variant: Variant;
}

interface TaxFields {
  nalog_request?: string;
  nalog_paid?: string;
  nalog_end_date?: string;
}

interface DiagnosticFields {
  diagnostic_status?: string;
  diagnostic_record_date?: string;
  diagnostic_end_date?: string;
  diagnostic_passed?: string;
  diagnostic_skipped?: string;
}

interface Invoice {
  serial: string;
  status: string;
  amount: number;
  payer: string | null;
  issue_date: string;
  paid_at: string;
  created_at: string;
  confirmed_at: string;
  canceled_at: string;
}

interface Application extends TaxFields, DiagnosticFields {
  id: number;
  document_id?: number | string;
  document: { ru: string; tj?: string; en?: string };
  category: { title: { ru: string }; icon_id: string | null };
  cabinet_type: string | null;
  applicant_type: string | null;
  name: string | null;
  phone: string | null;
  status: {
    key: string;
    title: { ru: string };
    background_color: string | null;
  };
  review_status: { key: string; title: { ru: string } };
  vehicle_number: string | null;
  vehicle_vin: string | null;
  invoices: Invoice[];
}

/* ------------------------------------------------------------------ */
/* Справочники и форматирование                                        */
/* ------------------------------------------------------------------ */

const SEARCH_OPTIONS: Record<
  SearchType,
  { label: string; placeholder: string }
> = {
  id: { label: "Номер заявки", placeholder: "Например, 367878" },
  order_id: { label: "Номер инвойса", placeholder: "Например, 00217305" },
  vehicle_number: {
    label: "Номер автомобиля",
    placeholder: "Например, 8415DD02",
  },
  vin: { label: "VIN-код", placeholder: "Например, LDP45B966SG421988" },
};

const CABINET_TYPES: Record<string, string> = {
  GUEST_OFFICE: "Гостевой кабинет",
  FRONT_OFFICE: "Фронт-офис",
  BACK_OFFICE: "Бэк-офис",
};

const APPLICANT_TYPES: Record<string, string> = {
  INDIVIDUAL: "Физическое лицо",
  LEGAL_ENTITY: "Юридическое лицо",
  INDIVIDUAL_ENTREPRENEUR: "Индивидуальный предприниматель",
};

const INVOICE_STATUSES: Record<string, { label: string; variant: Variant }> = {
  OPEN: { label: "Ожидает оплаты", variant: "neutral" },
  PAID: { label: "Оплачен", variant: "success" },
  CONFIRMED: { label: "Подтверждён", variant: "success" },
  CANCELED: { label: "Отменён", variant: "neutral" },
  CANCELLED: { label: "Отменён", variant: "neutral" },
  EXPIRED: { label: "Просрочен", variant: "danger" },
};

const dictValue = (dict: Record<string, string>, value?: string | null) =>
  value ? (dict[value] ?? value) : "—";

const formatYesNo = (value?: string) =>
  value === "true" ? "Да" : value === "false" ? "Нет" : "—";

const formatTaxResponse = (value?: string) =>
  value === "true" ? "Получен" : value === "false" ? "Не получен" : "—";

const formatDiagnosticStatus = (value?: string) =>
  value === "PASS" ? "Исправен" : value === "FAIL" ? "Неисправен" : "—";

const formatDate = (value?: string | null) => {
  if (!value) return "—";
  const trimmed = value.trim();

  const iso = trimmed.match(/^(\d{4})[-/.](\d{2})[-/.](\d{2})/);
  if (iso) return `${iso[3]}.${iso[2]}.${iso[1]}`;

  const ru = trimmed.match(/^(\d{2})[./-](\d{2})[./-](\d{4})/);
  if (ru) return `${ru[1]}.${ru[2]}.${ru[3]}`;

  return trimmed.split(/[ T]/)[0];
};

const formatDateTime = (value?: string | null) => {
  if (!value) return "—";
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})/);
  return match
    ? `${match[3]}.${match[2]}.${match[1]} ${match[4]}:${match[5]}`
    : formatDate(value);
};

const amountFormatter = new Intl.NumberFormat("ru-RU", {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const formatAmount = (value: number) => `${amountFormatter.format(value)} сом`;

/* ------------------------------------------------------------------ */
/* Логика налога, диагностики и счетов                                 */
/* ------------------------------------------------------------------ */

const TECH_INSPECTION_DOCUMENT_ID: number | null = 2480;

const TECH_INSPECTION_DOCUMENT = {
  ru: "Талон (листовка) технического осмотра",
  en: "Technical inspection coupon",
};

const isTechInspection = (application: Application) => {
  if (
    TECH_INSPECTION_DOCUMENT_ID !== null &&
    application.document_id !== undefined
  ) {
    return (
      String(application.document_id) === String(TECH_INSPECTION_DOCUMENT_ID)
    );
  }
  return (
    application.document.en === TECH_INSPECTION_DOCUMENT.en ||
    application.document.ru === TECH_INSPECTION_DOCUMENT.ru
  );
};

const EMPTY_VALUES = ["", "-", "—", "–", "null", "undefined"];

const isPresent = (value: unknown): boolean => {
  if (value === null || value === undefined) return false;
  return !EMPTY_VALUES.includes(String(value).trim().toLowerCase());
};

const hasVehicle = (a: Application) =>
  isPresent(a.vehicle_number) || isPresent(a.vehicle_vin);

const shouldShowTax = (a: Application) =>
  isTechInspection(a) && hasTaxFields(a);

const shouldShowDiagnostic = (a: Application) =>
  isTechInspection(a) && hasDiagnosticFields(a);

const hasTaxFields = (t: TaxFields) =>
  isPresent(t.nalog_request) ||
  isPresent(t.nalog_paid) ||
  isPresent(t.nalog_end_date);

const getTaxInfo = (t: TaxFields): StateInfo => {
  if (t.nalog_paid === "true") {
    return { state: "Налог оплачен", short: "Оплачен", variant: "success" };
  }
  if (t.nalog_request === "false") {
    return {
      state: "Налоговая не ответила, проверить оплату не удалось",
      short: "Нет ответа",
      variant: "warning",
    };
  }
  if (t.nalog_paid === "false" && t.nalog_end_date) {
    return {
      state: "Срок действия недостаточен",
      short: "Срок недостаточен",
      variant: "warning",
    };
  }
  if (t.nalog_paid === "false") {
    return {
      state: "Налог не оплачен",
      short: "Не оплачен",
      variant: "danger",
    };
  }
  return { state: "Нет данных", short: "Нет данных", variant: "neutral" };
};

const hasDiagnosticFields = (d: DiagnosticFields) =>
  isPresent(d.diagnostic_status) ||
  isPresent(d.diagnostic_record_date) ||
  isPresent(d.diagnostic_end_date) ||
  isPresent(d.diagnostic_passed) ||
  isPresent(d.diagnostic_skipped);

const getDiagnosticInfo = (d: DiagnosticFields): StateInfo => {
  if (d.diagnostic_skipped === "true") {
    return {
      state: "Диагностика не требуется",
      short: "Не требуется",
      variant: "neutral",
    };
  }
  if (d.diagnostic_passed === "true") {
    return {
      state: "Диагностика пройдена",
      short: "Пройдена",
      variant: "success",
    };
  }
  if (d.diagnostic_status === "FAIL") {
    return {
      state: "Транспорт неисправен",
      short: "Неисправен",
      variant: "danger",
    };
  }
  if (d.diagnostic_status === "PASS" && d.diagnostic_passed === "false") {
    return {
      state:
        "С момента выдачи диагностики прошло более 30 дней документ является недействительным",
      short: "Истёк срок",
      variant: "warning",
    };
  }
  return {
    state: "Диагностика не найдена",
    short: "Не найдена",
    variant: "danger",
  };
};

const isPaid = (invoice: Invoice) =>
  invoice.status === "PAID" || invoice.status === "CONFIRMED";

const getInvoicesInfo = (invoices: Invoice[]): StateInfo => {
  const total = invoices.length;
  const paid = invoices.filter(isPaid).length;

  if (total === 0) {
    return { state: "Счетов нет", short: "Нет счетов", variant: "neutral" };
  }
  if (paid === total) {
    const text = total === 1 ? "Оплачен" : `Оплачены (${total})`;
    return { state: text, short: text, variant: "success" };
  }
  if (paid === 0) {
    const text = total === 1 ? "Не оплачен" : `Не оплачены (${total})`;
    return { state: text, short: text, variant: "neutral" };
  }
  const text = `Оплачено ${paid} из ${total}`;
  return { state: text, short: text, variant: "neutral" };
};

const Tag: FC<{ variant: Variant; children: string }> = ({
  variant,
  children,
}) => <span className={`ors-tag ors-tag--${variant}`}>{children}</span>;

const CopyIcon: FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <rect
      x="5"
      y="5"
      width="9"
      height="9"
      rx="1.5"
      stroke="currentColor"
      strokeWidth="1.4"
    />
    <path
      d="M11 5V3.5A1.5 1.5 0 0 0 9.5 2h-6A1.5 1.5 0 0 0 2 3.5v6A1.5 1.5 0 0 0 3.5 11H5"
      stroke="currentColor"
      strokeWidth="1.4"
    />
  </svg>
);

const CheckIcon: FC = () => (
  <svg
    width="14"
    height="14"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M3 8.5l3 3 7-7"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronIcon: FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <path
      d="M6 4l4 4-4 4"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const SearchIcon: FC = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    aria-hidden="true"
  >
    <circle cx="7" cy="7" r="4.75" stroke="currentColor" strokeWidth="1.5" />
    <path
      d="M10.5 10.5L14 14"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
    />
  </svg>
);

const CopyButton: FC<{ value: string; label: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async (event: MouseEvent) => {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      /* буфер обмена недоступен */
    }
  };

  return (
    <button
      type="button"
      className={`ors-copy ${copied ? "is-copied" : ""}`}
      onClick={handleCopy}
      aria-label={copied ? "Скопировано" : `Скопировать: ${label}`}
      title={copied ? "Скопировано" : "Скопировать"}
    >
      {copied ? <CheckIcon /> : <CopyIcon />}
    </button>
  );
};

const Field: FC<{
  label: string;
  value?: string | number | null;
  copy?: boolean;
  mono?: boolean;
  children?: ReactNode;
}> = ({ label, value, copy, mono, children }) => {
  const text =
    value === null || value === undefined || value === "" ? "—" : String(value);

  return (
    <div className="ors-field">
      <dt>{label}</dt>
      <dd>
        {children ?? (
          <span className={mono ? "ors-mono" : undefined}>{text}</span>
        )}
        {copy && text !== "—" && <CopyButton value={text} label={label} />}
      </dd>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Подробности заявки                                                  */
/* ------------------------------------------------------------------ */

const InvoicesTable: FC<{ invoices: Invoice[] }> = ({ invoices }) => {
  if (invoices.length === 0) {
    return <p className="ors-muted">Счетов нет</p>;
  }

  return (
    <div className="ors-scroll">
      <table className="ors-subtable">
        <thead>
          <tr>
            <th scope="col">Номер счёта</th>
            <th scope="col" className="ors-num">
              Сумма
            </th>
            <th scope="col">Статус</th>
            <th scope="col">Выставлен</th>
            <th scope="col">Оплачен</th>
            <th scope="col">Подтверждён</th>
            <th scope="col">Плательщик</th>
          </tr>
        </thead>
        <tbody>
          {invoices.map((invoice) => {
            const status = INVOICE_STATUSES[invoice.status] ?? {
              label: invoice.status,
              variant: "neutral" as Variant,
            };

            return (
              <tr key={invoice.serial}>
                <td data-label="Номер счёта">
                  <span className="ors-mono">{invoice.serial}</span>
                  <CopyButton value={invoice.serial} label="номер счёта" />
                </td>
                <td data-label="Сумма" className="ors-num">
                  {formatAmount(invoice.amount)}
                </td>
                <td data-label="Статус">
                  <Tag variant={status.variant}>{status.label}</Tag>
                </td>
                <td data-label="Выставлен">
                  {formatDateTime(invoice.issue_date)}
                </td>
                <td data-label="Оплачен">{formatDateTime(invoice.paid_at)}</td>
                <td data-label="Подтверждён">
                  {formatDateTime(invoice.confirmed_at)}
                </td>
                <td data-label="Плательщик">{invoice.payer || "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

const ApplicationDetails: FC<{
  application: Application;
  showTax: boolean;
  showDiagnostic: boolean;
}> = ({ application, showTax, showDiagnostic }) => {
  const taxInfo = getTaxInfo(application);
  const diagnosticInfo = getDiagnosticInfo(application);

  return (
    <div className="ors-details">
      <div className="ors-details__grid">
        <section>
          <h4 className="ors-details__title">Заявка</h4>
          <dl className="ors-fields">
            <Field label="Номер" value={application.id} copy mono />
            <Field label="Категория">
              <span className="ors-category">
                {application.category.icon_id && (
                  <img
                    src={application.category.icon_id}
                    alt=""
                    className="ors-category__icon"
                    onError={(event) => {
                      event.currentTarget.style.display = "none";
                    }}
                  />
                )}
                {application.category.title.ru}
              </span>
            </Field>
            <Field label="Статус">
              <Tag variant="info">{application.status.title.ru}</Tag>
            </Field>
            <Field label="Рассмотрение">
              <Tag variant="purple">{application.review_status.title.ru}</Tag>
            </Field>
          </dl>
        </section>

        <section>
          <h4 className="ors-details__title">Заявитель</h4>
          <dl className="ors-fields">
            <Field label="Имя" value={application.name} />
            <Field label="Телефон" value={application.phone} copy mono />
            <Field
              label="Тип заявителя"
              value={dictValue(APPLICANT_TYPES, application.applicant_type)}
            />
            <Field
              label="Кабинет"
              value={dictValue(CABINET_TYPES, application.cabinet_type)}
            />
          </dl>
        </section>

        {hasVehicle(application) && (
          <section>
            <h4 className="ors-details__title">Транспорт</h4>
            <dl className="ors-fields">
              {isPresent(application.vehicle_number) && (
                <Field
                  label="Номер автомобиля"
                  value={application.vehicle_number}
                  copy
                  mono
                />
              )}
              {isPresent(application.vehicle_vin) && (
                <Field label="VIN" value={application.vehicle_vin} copy mono />
              )}
            </dl>
          </section>
        )}

        {showTax && (
          <section>
            <h4 className="ors-details__title">
              Налог на транспорт
              <Tag variant={taxInfo.variant}>{taxInfo.state}</Tag>
            </h4>
            <dl className="ors-fields">
              {/* Показываем только когда налоговая не ответила */}
              {application.nalog_request === "false" && (
                <Field
                  label="Ответ налоговой"
                  value={formatTaxResponse(application.nalog_request)}
                />
              )}
              <Field
                label="Налог оплачен"
                value={
                  application.nalog_request === "false"
                    ? "Неизвестно"
                    : formatYesNo(application.nalog_paid)
                }
              />
              <Field
                label="Налог действует до"
                value={formatDate(application.nalog_end_date)}
              />
            </dl>
            {application.nalog_request === "false" ? (
              <p className="ors-hint">
                Сервис налоговой не ответил. Повторите проверку позже.
              </p>
            ) : (
              taxInfo.variant !== "success" && (
                <p className="ors-hint">
                  Если гражданин уже оплатил налог, ему нужно нажать «Проверить
                  статус всех платежей», затем повторить поиск.
                </p>
              )
            )}
          </section>
        )}

        {showDiagnostic && (
          <section>
            <h4 className="ors-details__title">
              Диагностика
              <Tag variant={diagnosticInfo.variant}>{diagnosticInfo.state}</Tag>
            </h4>
            <dl className="ors-fields">
              {isPresent(application.diagnostic_skipped) && (
                <Field
                  label="Освобождён от диагностики"
                  value={formatYesNo(application.diagnostic_skipped)}
                />
              )}
              <Field
                label="Результат"
                value={formatDiagnosticStatus(application.diagnostic_status)}
              />
              <Field
                label="Дата прохождения"
                value={formatDate(application.diagnostic_record_date)}
              />
              <Field
                label="Действует до"
                value={formatDate(application.diagnostic_end_date)}
              />
              <Field
                label="Диагностика пройдена"
                value={formatYesNo(application.diagnostic_passed)}
              />
            </dl>
          </section>
        )}
      </div>

      <section className="ors-details__invoices">
        <h4 className="ors-details__title">
          Счета на оплату
          <span className="ors-counter">{application.invoices.length}</span>
        </h4>
        <InvoicesTable invoices={application.invoices} />
      </section>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/* Строка заявки                                                       */
/* ------------------------------------------------------------------ */

interface ColumnsVisibility {
  tax: boolean;
  diagnostic: boolean;
  vehicle: boolean;
}

const ApplicationRow: FC<{
  application: Application;
  columns: ColumnsVisibility;
  isOpen: boolean;
  onToggle: () => void;
}> = ({ application, columns, isOpen, onToggle }) => {
  const showTax = shouldShowTax(application);
  const showDiagnostic = shouldShowDiagnostic(application);
  const columnsCount =
    3 +
    Number(columns.tax) +
    Number(columns.diagnostic) +
    Number(columns.vehicle);
  const taxInfo = getTaxInfo(application);
  const diagnosticInfo = getDiagnosticInfo(application);
  const invoicesInfo = getInvoicesInfo(application.invoices);
  const detailsId = `ors-details-${application.id}`;

  return (
    <Fragment>
      <tr className={`ors-row ${isOpen ? "is-open" : ""}`} onClick={onToggle}>
        <td data-label="Заявка" className="ors-row__id">
          <button
            type="button"
            className="ors-row__toggle"
            aria-expanded={isOpen}
            aria-controls={detailsId}
            onClick={(event) => {
              event.stopPropagation();
              onToggle();
            }}
          >
            <span className="ors-row__chevron">
              <ChevronIcon />
            </span>
            {application.id}
          </button>
        </td>
        <td data-label="Услуга" className="ors-row__service">
          {application.document.ru}
        </td>
        {columns.tax && (
          <td
            data-label="Налог"
            className={showTax ? undefined : "ors-cell--empty"}
          >
            {showTax ? (
              <Tag variant={taxInfo.variant}>{taxInfo.short}</Tag>
            ) : null}
          </td>
        )}
        {columns.diagnostic && (
          <td
            data-label="Диагностика"
            className={showDiagnostic ? undefined : "ors-cell--empty"}
          >
            {showDiagnostic ? (
              <Tag variant={diagnosticInfo.variant}>{diagnosticInfo.short}</Tag>
            ) : null}
          </td>
        )}
        <td data-label="Счета">
          <Tag variant={invoicesInfo.variant}>{invoicesInfo.short}</Tag>
        </td>
        {columns.vehicle && (
          <td
            data-label="Номер авто"
            className={`ors-mono ors-row__vehicle${
              isPresent(application.vehicle_number) ? "" : " ors-cell--empty"
            }`}
          >
            {isPresent(application.vehicle_number)
              ? application.vehicle_number
              : null}
          </td>
        )}
      </tr>

      {isOpen && (
        <tr id={detailsId} className="ors-row-details">
          <td colSpan={columnsCount}>
            <ApplicationDetails
              application={application}
              showTax={showTax}
              showDiagnostic={showDiagnostic}
            />
          </td>
        </tr>
      )}
    </Fragment>
  );
};

/* ------------------------------------------------------------------ */
/* Страница                                                            */
/* ------------------------------------------------------------------ */

const SEARCH_TYPE_KEY = "operator-search-type";

const readSavedSearchType = (): SearchType => {
  try {
    const saved = localStorage.getItem(SEARCH_TYPE_KEY);
    if (saved && saved in SEARCH_OPTIONS) return saved as SearchType;
  } catch {
    /* localStorage недоступен */
  }
  return "id";
};

const OperatorApplicationStatus: FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const { applications, isLoading, error } = useSelector(
    (state: RootState) => state.application,
  );
  const list = applications as unknown as Application[];

  const [searchType, setSearchType] = useState<SearchType>(readSavedSearchType);
  const [searchValue, setSearchValue] = useState("");
  const [hasSearched, setHasSearched] = useState(false);
  const [openIds, setOpenIds] = useState<Set<number>>(new Set());

  useEffect(() => {
    return () => {
      dispatch(applicationSlice.actions.applicationReset());
    };
  }, [dispatch]);

  useEffect(() => {
    setOpenIds(new Set());
  }, [applications]);

  const handleSearch = (event?: FormEvent) => {
    event?.preventDefault();
    const value = searchValue.trim();
    if (!value) return;

    setHasSearched(true);

    if (searchType === "id") {
      dispatch(getApplicationInfo({ id: value }));
      return;
    }
    if (searchType === "order_id") {
      dispatch(getApplicationInfo({ order_id: value }));
      return;
    }
    if (searchType === "vin") {
      dispatch(getApplicationInfo({ vin: value }));
      return;
    }
    dispatch(getApplicationInfo({ vehicle_number: value }));
  };

  const handleSearchTypeChange = (value: SearchType) => {
    setSearchType(value);
    setSearchValue("");
    setHasSearched(false);
    dispatch(applicationSlice.actions.applicationReset());
    try {
      localStorage.setItem(SEARCH_TYPE_KEY, value);
    } catch {
      /* localStorage недоступен */
    }
  };

  const toggleApplication = (id: number) => {
    setOpenIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOpen = list.length > 0 && openIds.size === list.length;

  const columns: ColumnsVisibility = {
    tax: list.some(shouldShowTax),
    diagnostic: list.some(shouldShowDiagnostic),
    vehicle: list.some(hasVehicle),
  };
  const showList = hasSearched && !isLoading && !error && list.length > 0;

  return (
    <div className="ors">
      <header className="ors-header">
        <h2 className="ors-header__title">
          Заявки
          {showList && <span className="ors-counter">{list.length}</span>}
        </h2>
      </header>

      <form className="ors-search" onSubmit={handleSearch} role="search">
        <label htmlFor="ors-search-type" className="ors-visually-hidden">
          Искать по
        </label>
        <select
          id="ors-search-type"
          className="ors-select"
          value={searchType}
          onChange={(event) =>
            handleSearchTypeChange(event.target.value as SearchType)
          }
        >
          {(Object.keys(SEARCH_OPTIONS) as SearchType[]).map((key) => (
            <option key={key} value={key}>
              {SEARCH_OPTIONS[key].label}
            </option>
          ))}
        </select>

        <div className="ors-search__input">
          <label htmlFor="ors-search-value" className="ors-visually-hidden">
            {SEARCH_OPTIONS[searchType].label}
          </label>
          <input
            id="ors-search-value"
            type="text"
            autoComplete="off"
            placeholder={SEARCH_OPTIONS[searchType].placeholder}
            value={searchValue}
            onChange={(event) => setSearchValue(event.target.value)}
          />
          <SearchIcon />
        </div>

        <button
          type="submit"
          className="ors-button"
          disabled={isLoading || !searchValue.trim()}
        >
          {isLoading ? "Поиск…" : "Найти"}
        </button>
      </form>

      <div className="ors-results" aria-live="polite" aria-busy={isLoading}>
        {hasSearched && isLoading && <p className="ors-message">Загрузка…</p>}

        {hasSearched && !isLoading && error && (
          <p className="ors-message ors-message--error" role="alert">
            {error}
          </p>
        )}

        {hasSearched && !isLoading && !error && list.length === 0 && (
          <p className="ors-message">
            Заявки не найдены. Проверьте номер и выбранный тип поиска.
          </p>
        )}

        {showList && (
          <>
            {list.length > 1 && (
              <div className="ors-toolbar">
                <button
                  type="button"
                  className="ors-link"
                  onClick={() =>
                    setOpenIds(
                      allOpen ? new Set() : new Set(list.map((a) => a.id)),
                    )
                  }
                >
                  {allOpen ? "Свернуть все" : "Развернуть все"}
                </button>
              </div>
            )}

            <table className="ors-table">
              <thead>
                <tr>
                  <th scope="col">Заявка</th>
                  <th scope="col">Услуга</th>
                  {columns.tax && <th scope="col">Налог</th>}
                  {columns.diagnostic && <th scope="col">Диагностика</th>}
                  <th scope="col">Счета</th>
                  {columns.vehicle && <th scope="col">Номер авто</th>}
                </tr>
              </thead>
              <tbody>
                {list.map((application) => (
                  <ApplicationRow
                    key={application.id}
                    application={application}
                    columns={columns}
                    isOpen={openIds.has(application.id)}
                    onToggle={() => toggleApplication(application.id)}
                  />
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </div>
  );
};

export default OperatorApplicationStatus;
